import { isRoad, tileAt, tileIndexAt, tracePath } from "./board.js";
import { MAX_BATTLE_TICKS, SIM_VERSION } from "./constants.js";
import { tileStats, unitStats } from "./data.js";
import { MILLI, mulPermille } from "./fixed.js";
import { hashOf } from "./hash.js";
import { applyHealing, applySabotage, resolveUnitAttacks } from "./abilities.js";
import { applyFireOrders } from "./damage.js";
import { buildTowers, fireTowers, type FireOrder, type TowerState } from "./towers.js";
import { validateLayout, validatePlan } from "./validate.js";
import { tileIndexAt as tileIndexOfProgress } from "./board.js";
import {
  UNIT_KINDS,
  type BattleEvent,
  type BattleInput,
  type BattleOutcome,
  type BattleResult,
  type Coord,
  type Layout,
  type Tick,
  type UnitKind,
  type UnitStatus,
} from "./types.js";

/**
 * Units within one wave enter 0.3 s apart so a wave arrives as a stream rather
 * than a single blob on the gate tile (02 §4).
 */
export const SPAWN_SPACING_TICKS = 3;

/** One tile of road costs this much travel. Positions are milli-tiles. */
const TILE = MILLI;

export interface UnitState {
  readonly id: number;
  readonly kind: UnitKind;
  hp: number;
  /** Milli-tiles travelled from the gate. `path.length * 1000` reaches the core. */
  progress: number;
  /** Tick this unit enters at the gate. */
  readonly spawnTick: Tick;
  status: UnitStatus;
  /** Set by a Frost tower. While in the future, movement is slowed. */
  slowUntilTick: Tick;
  slowPermille: number;
  /** Earliest tick this unit may use its ability again (Priest, Saboteur). */
  nextAbilityTick: Tick;
}

export interface BarricadeState {
  /** Index into the traced path. */
  readonly tileIndex: number;
  hp: number;
}

export interface BattleState {
  tick: Tick;
  readonly path: readonly Coord[];
  readonly units: readonly UnitState[];
  readonly barricades: readonly BarricadeState[];
  readonly towers: readonly TowerState[];
  readonly events: BattleEvent[];
  outcome: BattleOutcome;
}

export interface ArmySummary {
  readonly sent: number;
  reached: number;
  lost: number;
  survived: number;
}

export interface Reveal {
  /** How many tiles of the road, from the gate, the attacker may see. */
  readonly roadTilesWalked: number;
  /** Towers that fired, were damaged, or were disabled. */
  readonly towerIds: readonly number[];
}

export interface ResultSummary {
  readonly outcome: BattleOutcome;
  readonly ticks: Tick;
  readonly army: ArmySummary;
  readonly carryReaching: number;
  readonly reveal: Reveal;
}

/** Called after every tick. Used by the replay player to draw; never changes the result. */
export type Observer = (state: BattleState) => void;

/**
 * Resolve a battle deterministically.
 *
 * The tick order is fixed by 02 GDD §7 and must not be rearranged — it is what
 * makes the server, the client's replay worker and CI agree to the hit point:
 *
 *   1. spawn groups whose offset has arrived      <- S1-3, done
 *   2. resolve barricade attacks                  <- S1-6
 *   3. move units (speed x mud x frost)           <- S1-3, done
 *   4. apply Priest healing                       <- S1-6
 *   5. apply Saboteur disables                    <- S1-6
 *   6. towers acquire targets and fire            <- S1-4
 *   7. apply damage, remove the dead              <- S1-5
 *
 * The unimplemented steps are marked below rather than left out, so the order
 * stays visible and nobody has to guess where the next rule belongs.
 */
export function simulate(input: BattleInput, observe?: Observer): BattleResult {
  const state = createState(input);

  for (state.tick = 0; state.tick < MAX_BATTLE_TICKS; state.tick++) {
    // 1. spawn
    spawnDueUnits(state);

    // 2. units stopped at a barricade hit it; siege units hit towers too
    resolveUnitAttacks(abilityContext(state));

    // 3. move
    moveUnits(state, input.layout);

    // 4. Priest healing
    applyHealing(abilityContext(state));

    // 5. Saboteur disables
    applySabotage(abilityContext(state));

    // 6. towers acquire targets and fire
    const shots = towersFire(state);

    // 7. apply damage, remove the dead
    applyFireOrders(
      { tick: state.tick, path: state.path, units: state.units, towers: state.towers, events: state.events },
      shots
    );

    observe?.(state);

    if (isOver(state)) break;
  }

  retreatSurvivors(state);

  const ticks = Math.min(state.tick, MAX_BATTLE_TICKS);
  const outcome: BattleOutcome = state.units.some((u) => u.status === "reached")
    ? "breach"
    : "hold";

  const summary = {
    outcome,
    ticks,
    army: summariseArmy(state),
    carryReaching: carryReaching(state),
    reveal: revealed(state, outcome),
  };

  return {
    simVersion: SIM_VERSION,
    ...summary,
    // Not part of `summary`, and therefore not part of the hash — see the note
    // on BattleResult.returning for why that is deliberate rather than an
    // oversight.
    ...countByKind(state),
    events: state.events,
    hash: hashResult(input, summary),
  };
}

/**
 * Who walks home and who does not, counted by kind.
 *
 * Everything that is not dead comes back: a unit that reached the core did its
 * job and returns with the loot, a unit still on the road when the clock ran
 * out retreats (see `retreatSurvivors`), and a unit that never entered was
 * never at risk. Only `dead` is gone — which is what makes `army.lost` the
 * price of an attack, and a failed probe cheap.
 *
 * Both halves come out of one pass, so they cannot drift apart: whatever the
 * statuses are, `returning` and `losses` add up to what was sent.
 */
function countByKind(state: BattleState): {
  returning: Partial<Record<UnitKind, number>>;
  losses: Partial<Record<UnitKind, number>>;
} {
  const returning: Partial<Record<UnitKind, number>> = {};
  const losses: Partial<Record<UnitKind, number>> = {};
  for (const unit of state.units) {
    const into = unit.status === "dead" ? losses : returning;
    into[unit.kind] = (into[unit.kind] ?? 0) + 1;
  }
  return { returning, losses };
}

/**
 * Anything still on the road when the clock runs out walks home (Assumptions:
 * "Units not at the core by then retreat as survivors"). They are not losses —
 * the attacker gets them back, which is what makes a failed probe cheap.
 */
function retreatSurvivors(state: BattleState): void {
  for (const unit of state.units) {
    if (unit.status === "walking" || unit.status === "blocked") unit.status = "retreated";
  }
}

function summariseArmy(state: BattleState): ArmySummary {
  const summary: ArmySummary = { sent: state.units.length, reached: 0, lost: 0, survived: 0 };
  for (const unit of state.units) {
    if (unit.status === "reached") summary.reached += 1;
    else if (unit.status === "dead") summary.lost += 1;
    else summary.survived += 1; // retreated, or never entered
  }
  return summary;
}

/**
 * Total Carry of the units that reached the core.
 *
 * This is a CAPACITY, not the loot. What actually comes home is capped against
 * the defender's storage — 25% of what sits above the protected 20% (02 §5) —
 * and core-sim knows nothing about a village's storage, deliberately. The
 * server applies that cap.
 */
function carryReaching(state: BattleState): number {
  let carry = 0;
  for (const unit of state.units) {
    if (unit.status === "reached") carry += unitStats(unit.kind).carry;
  }
  return carry;
}

/**
 * What the attacker earned the right to see (02 GDD §7.3, D-028): the road they
 * actually walked, and the towers that did something to them.
 */
function revealed(state: BattleState, outcome: BattleOutcome): Reveal {
  let furthest = -1;
  for (const unit of state.units) {
    if (unit.status === "waiting") continue;
    furthest = Math.max(furthest, tileIndexOfProgress(unit.progress, state.path.length));
  }

  return {
    roadTilesWalked: outcome === "breach" ? state.path.length : furthest + 1,
    towerIds: state.towers.filter((t) => t.involved).map((t) => t.id),
  };
}

/**
 * The determinism fingerprint: FNV-1a over a canonical JSON encoding of the
 * inputs and everything the simulation concluded from them.
 *
 * The event stream is included, so two builds that reach the same outcome by
 * different routes still fail the comparison. That is the point — a replay must
 * play back identically, not merely end the same way.
 */
export function hashResult(input: BattleInput, summary: ResultSummary): string {
  return hashOf({ input, ...summary });
}

// ---------------------------------------------------------------------------
// setup
// ---------------------------------------------------------------------------

export function createState(input: BattleInput): BattleState {
  const layoutCheck = validateLayout(input.layout);
  if (!layoutCheck.ok) {
    throw new Error(`Invalid layout: ${layoutCheck.error.code} ${layoutCheck.error.message}`);
  }
  const planCheck = validatePlan(input.plan);
  if (!planCheck.ok) {
    throw new Error(`Invalid plan: ${planCheck.error.code} ${planCheck.error.message}`);
  }

  const traced = tracePath(input.layout);
  if (!traced.ok) {
    // validateLayout already ran the same trace, so this cannot happen; the
    // check keeps the types honest instead of asserting.
    throw new Error(`Invalid layout: ${traced.code}`);
  }

  return {
    tick: 0,
    path: traced.path,
    units: flattenWaves(input),
    barricades: findBarricades(input.layout, traced.path),
    towers: buildTowers(input.layout),
    events: [],
    outcome: "hold",
  };
}

/**
 * Turn the wave plan into one ordered list of units with fixed spawn ticks.
 *
 * Ids are assigned here, in wave order then entry order, so they do not depend
 * on the order things happen during the battle. Two runs always number the same
 * unit the same way, which is what makes the replay fingerprint stable.
 */
function flattenWaves(input: BattleInput): UnitState[] {
  const units: UnitState[] = [];
  let id = 0;

  input.plan.waves.forEach((wave) => {
    wave.units.forEach((kind, indexInWave) => {
      units.push({
        id: id++,
        kind,
        hp: unitStats(kind).hp,
        progress: 0,
        spawnTick: wave.offsetTicks + indexInWave * SPAWN_SPACING_TICKS,
        status: "waiting",
        slowUntilTick: -1,
        slowPermille: MILLI,
        nextAbilityTick: 0,
      });
    });
  });

  return units;
}

function findBarricades(layout: Layout, path: readonly Coord[]): BarricadeState[] {
  const barricades: BarricadeState[] = [];
  path.forEach((at, tileIndex) => {
    if (tileAt(layout, at.x, at.y)?.kind !== "barricade") return;
    const stats = tileStats("barricade");
    const base = stats.hp ?? 0;
    const bonus = mulPermille(base, (stats.hpPerWallLevelPermille ?? 0) * layout.wallLevel);
    barricades.push({ tileIndex, hp: base + bonus });
  });
  return barricades;
}

// ---------------------------------------------------------------------------
// 1. spawning
// ---------------------------------------------------------------------------

function spawnDueUnits(state: BattleState): void {
  for (const unit of state.units) {
    if (unit.status !== "waiting") continue;
    if (unit.spawnTick > state.tick) continue;
    unit.status = "walking";
    state.events.push({
      tick: state.tick,
      type: "spawn",
      a: unit.id,
      b: UNIT_KINDS.indexOf(unit.kind),
    });
  }
}

// ---------------------------------------------------------------------------
// 3. movement
// ---------------------------------------------------------------------------

/**
 * Advance every walking unit by one tick.
 *
 * Speed is taken from the tile the unit stands on at the START of the tick, so
 * the order units are processed in cannot change the result. The fastest unit
 * covers a quarter of a tile per tick, so a unit can cross at most one tile
 * boundary per tick — but the code clamps at the boundary anyway rather than
 * relying on that.
 */
function moveUnits(state: BattleState, layout: Layout): void {
  const goal = state.path.length * TILE;

  for (const unit of state.units) {
    if (unit.status !== "walking" && unit.status !== "blocked") continue;

    const fromTile = tileIndexAt(unit.progress, state.path.length);
    const step = stepFor(unit, state, layout, fromTile);

    if (step === 0) {
      // Standing on something that stops it dead — currently only a barricade.
      unit.status = "blocked";
      continue;
    }

    let next = unit.progress + step;
    const intoTile = tileIndexAt(next, state.path.length);

    if (intoTile !== fromTile) {
      const blocking = liveBarricadeAt(state, intoTile);
      if (blocking) {
        // Queue up against it instead of walking onto it.
        next = intoTile * TILE;
        if (unit.status !== "blocked") {
          unit.status = "blocked";
          state.events.push({ tick: state.tick, type: "blocked", a: unit.id, b: intoTile });
        }
        unit.progress = next;
        continue;
      }
    }

    unit.status = "walking";
    unit.progress = next;

    if (unit.progress >= goal) {
      unit.progress = goal;
      unit.status = "reached";
      state.events.push({ tick: state.tick, type: "reach", a: unit.id });
    }
  }
}

/** Milli-tiles this unit travels this tick: base speed x tile x frost. */
function stepFor(
  unit: UnitState,
  state: BattleState,
  layout: Layout,
  tileIndex: number
): number {
  if (liveBarricadeAt(state, tileIndex)) return 0;

  const at = state.path[tileIndex];
  if (!at) return 0;

  const kind = tileAt(layout, at.x, at.y)?.kind;
  if (!isRoad(kind)) return 0;

  const terrain = tileStats(kind === "mud" ? "mud" : "road").speedPermille;
  const slowed = state.tick <= unit.slowUntilTick ? unit.slowPermille : MILLI;

  const base = unitStats(unit.kind).speedMilliPerTick;
  return mulPermille(mulPermille(base, terrain), slowed);
}

function liveBarricadeAt(state: BattleState, tileIndex: number): BarricadeState | undefined {
  return state.barricades.find((b) => b.tileIndex === tileIndex && b.hp > 0);
}

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// 6. towers
// ---------------------------------------------------------------------------

function abilityContext(state: BattleState) {
  return {
    tick: state.tick,
    path: state.path,
    units: state.units,
    barricades: state.barricades,
    towers: state.towers,
    events: state.events,
  };
}

function towersFire(state: BattleState): FireOrder[] {
  const orders = fireTowers(state.towers, {
    tick: state.tick,
    path: state.path,
    units: state.units,
  });

  for (const order of orders) {
    state.events.push({
      tick: state.tick,
      type: "fire",
      a: order.towerId,
      b: order.unitId,
    });
  }

  return orders;
}

// ---------------------------------------------------------------------------

function isOver(state: BattleState): boolean {
  // Blocked units are still in the battle: a Ram or Militia will clear the
  // barricade in S1-6. Until then they simply run out the clock.
  return state.units.every((u) => u.status === "reached" || u.status === "dead");
}

/** The position of a unit in board coordinates, for the renderer. */
export function positionOf(state: BattleState, unit: UnitState): Coord | undefined {
  return state.path[tileIndexAt(unit.progress, state.path.length)];
}
